import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import ContatoDialog from 'dialogs/ContatoDialog';
import OpcoesParcelamentoDialog from 'dialogs/OpcoesParcelamentoDialog';
import api from 'services/api';
import { useAuth } from 'contexts/auth';
import styled from 'styled-components';
import ChatIcon from 'images/chat.svg';
import { When } from 'react-if';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DebitoContaDialog from 'dialogs/DebitoContaDialog';
import { toast } from 'react-toastify';
import { encryptData } from 'utils/crypto';
import { MENSAGEM_ERRO_OPCOES_PAGAMENTO } from 'utils/constants'
import { IDivida } from 'interfaces/Divida';
import { IAcordo } from 'interfaces/Acordo';
import {
  IBuscaOpcaoPagamento,
  IOpcaoPagamento,
} from 'interfaces/BuscaOpcaoPagamento';
import { HelperButton, HelperContainer, HelperText } from './styles';
import { buscaDividaService } from 'services/buscaDivida/buscaDivida';
import { buscaAcordoService } from 'services/buscaAcordo/buscaAcordo';
import secureLocalStorage from 'react-secure-storage';
import { useTracking } from 'contexts/tracking';
import { enableBodyScroll } from 'utils/scroll-lock';
import { DividaLista } from "../../components/DividaLista";
import { AcaoEvento } from 'services/TrackingService';
import { buscaHonorariosService } from 'services/buscaCredores/buscaCredores';
import ModalHonorario from 'components/Modal/ModalHonorario';

const ButtonCall = styled(Button)`
  position: fixed;
  z-index: 10;
  bottom: 0;
  right: 100px;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
`;

export type ParcelamentoRequestProps = {
  divida?: IDivida;
  parcelamento?: IOpcaoPagamento;
  DataValidade?: Date;
  ValorEntrada?: number;
  novaOpcao: IBuscaOpcaoPagamento;
};

export default function Dashboard() {
  const [dividas, setDividas] = useState<IDivida[]>([]);
  const [acordos, setAcordos] = useState<IAcordo[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [dividaSelecionada, setDividaSelecionada] = useState<IDivida | null>(null);
  const [initialCateriaId, setInitialCateriaId] = useState<number | null>(null);
  const [opcao, setOpcao] = useState<IBuscaOpcaoPagamento>({});
  const [openDebitoConta, setOpenDebitoConta] = useState(false);
  const [debitoContaProps, setDebitoContaProps] = useState<any>(null);
  const [isReorg, setIsReorg] = useState(false);
  const { user, bank, dividasAuthContext, setDividasAuthContext, setAcordosAuthContext } = useAuth();
  const navigate = useNavigate();
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [loadingDividas, setLoadingDividas] = useState(true);
  const [loadingAcordos, setLoadingAcordos] = useState(true);
  const [isDialogError, setIsDialogError] = useState(false);
  const { trackId } = useParams<{ trackId: string | undefined }>()
  const { sendTracking } = useTracking();

  const [isModalHonorarioOpen, setModalHonorarioOpen] = useState(false);

  const carteiraIds = useMemo(
    () => [
      ...dividas.map(item => item.carteiraId),
      ...acordos.map(item => item.codCarteira),
    ],
    [dividas, acordos],
  );

  const carteiras = useMemo(
    () => [
      ...dividas.map(item => ({
        crmId: item.crm,
        carteiraId: item.carteiraId,
        fase: item.fase,
        ranking: item.ranking,
      })),
      ...acordos.map(item => ({
        crmId: item.crm,
        carteiraId: item.codCarteira,
        fase: item.fase,
        ranking: item.ranking,
      })),
    ],
    [dividas, acordos],
  );

  const opcoesPagamento = useMemo(() => opcao?.opcoesPagamento, [opcao]);

  useEffect(() => {
    if (loading) return;
    setTimeout(
      () => setShowHelp(true),
      parseInt(process.env.REACT_APP_HELP_TIMEOUT || '5000'),
    );
  }, [loading]);

  useEffect(
    () => {
      enableBodyScroll(document.body)
    }, []
  )

  const onClickEnviarAcordo = async ({ divida, opcaoPagamento }: any) => {
    localStorage.setItem('@dividaAcordo', encryptData(divida));
    localStorage.setItem(
      '@dividaAcordoOpacaoPagamento',
      encryptData(opcaoPagamento),
    );
    localStorage.setItem('@parcelamentoAcordo', encryptData(opcaoPagamento));

    navigate('/dashboard/enviar-acordo');
  };

  const onSubmitParcelamento = async ({
    divida = dividaSelecionada || {},
    parcelamento,
    DataValidade,
    ValorEntrada,
    novaOpcao,
  }: ParcelamentoRequestProps) => {
    if (parcelamento?.precisaDeAprovacao) {
      onClickEnviarAcordo({ divida, opcaoPagamento: parcelamento });
      return;
    }
    localStorage.setItem('@resumoBoletoParcelamento', encryptData(parcelamento));
    localStorage.setItem('@parcelamentoAcordo', encryptData(parcelamento));
    localStorage.setItem('@dataValidade', encryptData(DataValidade));
    localStorage.setItem('@dividaSucesso', encryptData(divida));
    localStorage.setItem('@valorEntrada', encryptData(ValorEntrada));

    setOpcao(novaOpcao);
    const usaResumoApi = !!novaOpcao?.chamarResumoBoleto;
    localStorage.setItem('@opcao', encryptData(novaOpcao));
    if (usaResumoApi) {
      setLoadingResumo(true);
      const token = secureLocalStorage.getItem('@token');
      const { data } = await api.post(
        'resumo-boleto',
        {
          CodigoCarteira: divida?.carteiraId,
          Contrato: divida?.numero,
          CNPJ_CPF: user?.cnpJ_CPF,
          CodigoOpcao: parcelamento?.codigo,
          Crm: divida?.crm,
          AceitaDebitoEmConta: parcelamento?.opcaoDebitoConta,
          AceitaChequeEspecial: parcelamento?.opcaoChequeEspecial
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!data.sucesso) {
        toast.warning(data.mensagem);
        return;
      }
      setLoadingResumo(false);
      localStorage.setItem('@resumoBoletoDivida', encryptData(data));
    }

    navigate('/dashboard/resumo-boleto');
    return;
  };

  const loadDividas = useCallback(async () => {
    const { data: newDividas } = await buscaDividaService({
      cnpJ_CPF: user?.cnpJ_CPF,
      crms: bank?.crms,
      financeira: bank?.financeira,
    });

    setDividas(newDividas);
    setDividasAuthContext(newDividas);
    setLoadingDividas(false);
  }, [user, bank, setDividasAuthContext]);

  const loadAcordos = useCallback(async () => {
    const { data: newAcordos } = await buscaAcordoService({
      cnpJ_CPF: user?.cnpJ_CPF,
      crms: bank?.crms,
      financeira: bank?.financeira,
    });

    if (newAcordos.length) {
      newAcordos.forEach(acordo => {
        acordo.contrato?.forEach(contrato => {
          sendTracking({
            trackId: trackId,
            acao: AcaoEvento.VisualizarAcordo,
            contratoId: contrato.numero,
            cpfCnpj: user?.cnpJ_CPF,
            crm: acordo.crm,
            carteiraId: acordo.codCarteira
          })
        })
      })
    }

    setAcordos(newAcordos);
    setAcordosAuthContext(newAcordos);
    setLoadingAcordos(false);
  }, [user, bank, setAcordosAuthContext]);

  useEffect(() => {
    if (!user || !bank) {
      return
    };

    const controller = new AbortController();
    async function loadCards() {
      try {
        setLoading(true);
        await Promise.all([loadDividas(), loadAcordos()]);
      } finally {
        setLoading(false);
      }
    }

    loadCards();
    return () => controller.abort();
  }, [user, bank, loadDividas, loadAcordos]);


  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data } = await buscaHonorariosService({
          headers: {
            Authorization: `Bearer ${secureLocalStorage.getItem("@token")}`,
          },
        });

        if (!mounted) return;
        setModalHonorarioOpen(Boolean(data));
      } catch (e) {
        console.error("Erro ao buscar honorários:", e);
      }
    })();

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (
      !dividas.length &&
      !acordos.length &&
      !loadingDividas &&
      !loadingAcordos &&
      !isModalHonorarioOpen
    ) {
      navigate('/sucesso');
      return;
    }
  }, [dividas, acordos, loadingDividas, loadingAcordos, isModalHonorarioOpen, navigate]);

  const opcaoHandleChange = useCallback((newOption: any) => {
    setOpcao(newOption);
  }, []);

  return (
    <div>
      <ModalHonorario
        open={isModalHonorarioOpen}
        onClose={() => setModalHonorarioOpen(false)}
      />
      <When condition={!!dividaSelecionada}>
        <OpcoesParcelamentoDialog
          opcaoHandleChange={opcaoHandleChange}
          loadingResumo={loadingResumo}
          onSubmit={({
            parcelamento,
            DataValidade,
            ValorEntrada,
            novaOpcao,
          }: {
            parcelamento: IOpcaoPagamento;
            DataValidade: Date;
            ValorEntrada: number;
            novaOpcao: IBuscaOpcaoPagamento;
          }) => {
            if (parcelamento?.opcaoDebitoEmConta) {
              setIsReorg(dividaSelecionada?.tipoContrato === "REORGPF")
              setOpenDebitoConta(true);
              setDebitoContaProps({ parcelamento, DataValidade, ValorEntrada });
              return;
            }

            onSubmitParcelamento({
              parcelamento,
              DataValidade,
              ValorEntrada,
              novaOpcao,
            });
          }}
          open={!!dividaSelecionada}
          divida={
            (!!dividaSelecionada &&
              dividasAuthContext?.find(
                _ => _.documento === dividaSelecionada.documento,
              )) ||
            dividaSelecionada
          }
          opcoesPagamento={opcoesPagamento}
          opcao={opcao}
          onClose={() => {
            setDividaSelecionada(null);
          }}
          usaResumoApi={!!opcao?.chamarResumoBoleto}
        />
      </When>

      <ContatoDialog
        onClose={() => {
          setIsDialogError(false)
          setOpen(false)
        }}
        open={open}
        carteiraIds={carteiraIds}
        carteiras={carteiras}
        initialCateriaId={initialCateriaId}
        customMessage={
          isDialogError ? MENSAGEM_ERRO_OPCOES_PAGAMENTO : ''
        }
      />

      <DebitoContaDialog
        isReorg={isReorg}
        open={openDebitoConta}
        onSubmit={
          ({ debitoConta = false, chequeEspecial = false }) => {
            const { parcelamento, DataValidade, dividaSelecionada }: any = debitoContaProps;
            const codigo = parcelamento.codigo;
            const opcaoDebitoConta = debitoConta;
            const opcaoChequeEspecial = chequeEspecial;

            onSubmitParcelamento({
              divida: dividaSelecionada,
              parcelamento: { ...parcelamento, codigo, opcaoDebitoConta, opcaoChequeEspecial },
              DataValidade,
              novaOpcao: opcao,
            });

            setOpenDebitoConta(false);
            setDebitoContaProps({});
          }
        }
        onClose={
          () => {
            setOpenDebitoConta(false);
            setDebitoContaProps({});
          }
        }
      />

      <When condition={showHelp}>
        <ButtonCall
          variant="contained"
          color="secondary"
          onClick={() => setOpen(true)}
          startIcon={<img src={ChatIcon} alt="Chat" />}
        >
          Precisa de ajuda?
        </ButtonCall>
      </When>

      {
        !!loading ?
          (
            <Box width="100%" display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )
          :
          (
            <DividaLista
              user={user}
              opcao={opcao}
              dividas={dividas}
              acordos={acordos}
              setOpen={setOpen}
              onClickEnviarAcordo={onClickEnviarAcordo}
              setInitialCateriaId={setInitialCateriaId}
              setDividaSelecionada={setDividaSelecionada}
              setIsReorg={setIsReorg}
              setOpenDebitoConta={setOpenDebitoConta}
              setOpcao={setOpcao}
              setDebitoContaProps={setDebitoContaProps}
              setIsDialogError={setIsDialogError}
              opcaoHandleChange={opcaoHandleChange}
              onSubmitParcelamento={onSubmitParcelamento}
            />
          )
      }
    </div>
  );
}
